import "./Diagram.scss";
import data from "../../data/technologies.json";
import * as d3 from "d3";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	arcGenerator,
	convertAlphaNumeric,
	createMatrix,
	findLastIndex,
	makeSorted,
	middleAngle,
	pointAsPercentage,
	pointGenerator,
	rotateAngle,
} from "../../utils/diagramUtils";
import orderBy from "lodash/orderBy";
import { useLocation } from "wouter";

const sortKeys = {
	alphabetical: { fields: ["title"], order: ["asc"] },
	category: { fields: ["category", "title"], order: ["asc", "asc"] },
	score: { fields: ["score", "title"], order: ["desc", "asc"] },
};

export function Diagram({
	zoomLevel,
	setZoomLevel,
	minScale = 0.5,
	maxScale = 4,
	sort = "category",
	hoveredCategory = "",
	hoveredItem = {},
	setHoveredItem,
	setHoveredItemMousePos,
	draggingMousePos,
	modalOpen,
	delay1 = 0.5,
	delay2 = 1,
	delay3 = 1.5,
}) {
	const ref = useRef();
	const zoomGroup = useRef();
	const zoom = useRef();
	const animationGroup = useRef();
	const radarWidthAsPercentage = useRef();
	const [hoversEnabled, setHoversEnabled] = useState(false);
	const prevCategory = useRef(hoveredCategory);
	const prevSort = useRef(sort);
	const [temporyHoverDetails, setTemporyHoverDetails] = useState(null);
	const hoversEnabledRef = useRef(false);

	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);

	const [, setLocation] = useLocation();

	const durationHover = 0.8;
	const durationSortChange = 1;

	const delaySort1 = 0;
	const delaySort2 = 0.03;
	const delaySort3 = 0.5;
	const delaySort4 = 0.8;

	const minWidthHover = 1024;

	useEffect(() => {
		hoversEnabledRef.current = hoversEnabled;
	});

	const updateDimensions = () => {
		const { width: w, height: h } = ref.current?.getBoundingClientRect();
		setWidth(w);
		setHeight(h);
	};

	const handleClickText = useCallback(
		(_, d) => {
			setLocation(`/tech-radar/${d.item.slug}`);
		},
		[setLocation]
	);

	const handleMouseOverText = useCallback(
		(e, d) => {
			if (window.innerWidth > minWidthHover) {
				const { clientX: x, clientY: y } = e;
				if (hoversEnabledRef.current) {
					setHoveredItem(d.item);
					setHoveredItemMousePos({ x, y });
					d3.selectAll(".score")
						.filter((d2) => d2.item.slug !== d.item.slug)
						.call((update) =>
							update
								.transition("mouseover")
								.duration(durationHover * 1000)
								.delay(0)
								.attr("opacity", 0.2)
						);
					d3.selectAll(".item-text")
						.filter((d2) => d2.item.slug !== d.item.slug)
						.call((update) =>
							update
								.transition("mouseover")
								.duration(durationHover * 1000)
								.delay(0)
								.attr("fill-opacity", 0.2)
						);
					d3.selectAll(".category")
						.filter((d2) => d2 !== d.item.category)
						.call((update) =>
							update
								.transition("mouseover")
								.duration(durationHover * 1000)
								.delay(0)
								.attr("opacity", 0.2)
						);
					d3.selectAll(".relationship")
						.filter(
							(d2) =>
								d2.sourceItem.slug !== d.item.slug &&
								d2.targetItem.slug !== d.item.slug
						)
						.call((update) =>
							update
								.transition("mouseover")
								.duration(durationHover * 1000)
								.delay(0)
								.attr("opacity", 0.1)
						);
				} else {
					setTemporyHoverDetails({
						item: d.item,
						mousePos: { clientX: x, clientY: y },
					});
				}
			}
		},
		[setHoveredItem, setHoveredItemMousePos]
	);

	const handleMouseOutText = useCallback(() => {
		if (hoversEnabledRef.current) {
			setHoveredItem({});
			setHoveredItemMousePos(null);
			d3.selectAll(".score").call((update) =>
				update
					.transition("mouseover")
					.duration(durationHover * 1000)
					.delay(0)
					.attr("opacity", 1)
			);
			d3.selectAll(".item-text").call((update) =>
				update
					.transition("mouseover")
					.duration(durationHover * 1000)
					.delay(0)
					.attr("fill-opacity", 1)
			);
			d3.selectAll(".category").call((update) =>
				update
					.transition("mouseover")
					.duration(durationHover * 1000)
					.delay(0)
					.attr("opacity", 1)
			);
			d3.selectAll(".relationship").call((update) =>
				update
					.transition("mouseover")
					.duration(durationHover * 1000)
					.delay(0)
					.attr("opacity", 1)
			);
		} else {
			setTemporyHoverDetails(null);
		}
	}, [setHoveredItem, setHoveredItemMousePos]);

	useEffect(() => {
		let prev = prevCategory.current;
		if (hoversEnabled) {
			if (hoveredCategory) {
				d3.selectAll(".score")
					.filter((d2) => d2.item.category.toLowerCase() !== hoveredCategory)
					.call((update) =>
						update
							.transition()
							.duration(durationHover * 1000)
							.delay(0)
							.attr("opacity", 0.2)
					);
				d3.selectAll(".item-text")
					.filter((d2) => d2.item.category.toLowerCase() !== hoveredCategory)
					.call((update) =>
						update
							.transition()
							.duration(durationHover * 1000)
							.delay(0)
							.attr("fill-opacity", 0.2)
					);
				d3.selectAll(".category")
					.filter((d2) => d2.toLowerCase() !== hoveredCategory)
					.call((update) =>
						update
							.transition()
							.duration(durationHover * 1000)
							.delay(0)
							.attr("opacity", 0.2)
					);
				d3.selectAll(".relationship")
					.filter(
						(d2) =>
							d2.sourceItem.category.toLowerCase() !== hoveredCategory &&
							d2.targetItem.category.toLowerCase() !== hoveredCategory
					)
					.call((update) =>
						update
							.transition()
							.duration(durationHover * 1000)
							.delay(0)
							.attr("opacity", 0.1)
					);
			} else if (hoveredCategory !== prev) {
				d3.selectAll(".score").call((update) =>
					update
						.transition()
						.duration(durationHover * 1000)
						.delay(0)
						.attr("opacity", 1)
				);
				d3.selectAll(".item-text").call((update) =>
					update
						.transition()
						.duration(durationHover * 1000)
						.delay(0)
						.attr("fill-opacity", 1)
				);
				d3.selectAll(".category").call((update) =>
					update
						.transition()
						.duration(durationHover * 1000)
						.delay(0)
						.attr("opacity", 1)
				);
				d3.selectAll(".relationship").call((update) =>
					update
						.transition()
						.duration(durationHover * 1000)
						.delay(0)
						.attr("opacity", 1)
				);
			}
		}
	}, [hoveredCategory, hoversEnabled]);

	const handleZoom = useCallback(
		({ transform, sourceEvent }) => {
			const currentZoomLevel = transform.k;
			setZoomLevel(currentZoomLevel);
			zoomGroup.current.attr("transform", transform);

			// update transformExtent based on scale
			const multiplier = (2 - currentZoomLevel) / (2 * currentZoomLevel);

			const radarWidthInPx =
				radarWidthAsPercentage.current * (width + height) * 0.666;

			zoom.current.translateExtent([
				[
					-width * multiplier - radarWidthInPx * 0.5,
					-height * multiplier - radarWidthInPx * 0.5,
				],
				[
					width * (multiplier + 1) + radarWidthInPx * 0.5,
					height * (multiplier + 1) + radarWidthInPx * 0.5,
				],
			]);

			const { clientX: x, clientY: y } = sourceEvent ?? {};
			draggingMousePos.set({ x, y });
		},
		[setZoomLevel, width, height, draggingMousePos]
	);
	const handleZoomEnd = useCallback(() => {
		draggingMousePos.set({});
	}, [draggingMousePos]);

	const setZoomGroup = () => {
		const svg = d3.select(ref.current);
		zoomGroup.current = svg.select(".zoom-group");
	};

	useEffect(() => {
		updateDimensions();
		setZoomGroup();
		window.addEventListener("resize", updateDimensions);

		return () => {
			window.removeEventListener("resize", updateDimensions);
		};
	}, []);

	// create diagram
	useEffect(() => {
		// set svg
		const svg = d3.select(ref.current);
		const diagramGroup = svg.select(".diagram-group");
		if (width + height > 0) {
			const sortedData = orderBy(
				data,
				sortKeys[sort].fields,
				sortKeys[sort].order
			);

			// set chord generator and generate chords
			const chordGenerator = d3.chord();
			const chords = chordGenerator(createMatrix(sortedData));
			// set categories to colours mapping
			const categories = ["Access", "Transaction", "Immersion"];
			const catColors = ["#4BE2A3", "#00A4DA", "#8834F2"];
			const catColor = d3.scaleOrdinal(categories, catColors);

			// set dimensions and scale
			const scale = d3
				.scaleLinear()
				.domain([0, 650])
				.range([0, (650 / 1920) * (width + height) * 0.666]);

			const chordRadius = scale(380 * 0.5);
			const scoringInnerRadius = scale(408 * 0.5);
			const scoringOuterRadius = scale(600 * 0.5);
			const catInnerRadius = scale(635 * 0.5);
			const catOuterRadius = scale(644 * 0.5);
			const textRadius = scale(670 * 0.5);
			const textSize = scale(14);
			const textPadding = scale(15);

			// transition reference
			const t = svg.transition().duration(0).delay(0);
			const sortTransition = svg.transition();

			// set categories arc generator
			const catArcGenerator = d3
				.arc()
				.innerRadius(catInnerRadius)
				.outerRadius(catOuterRadius);

			// generate gradient points
			const gradientPointGenerator = (chordIndex) => {
				const sourceAngle = middleAngle(
					chords.groups.find((el) => el.index === chordIndex).startAngle,
					chords.groups.find((el) => el.index === chordIndex).endAngle
				);
				const sourcePoint = pointAsPercentage(
					pointGenerator(sourceAngle, chordRadius),
					width,
					height
				);
				return sourcePoint;
			};

			// background circle
			diagramGroup.select(".bg-circle").attr("r", catInnerRadius);

			// scoring
			const score = diagramGroup
				.select(".scores-group")
				.selectAll("g")
				.data(
					chords.groups
						.filter((el) => el.value > 0)
						.map((d) => {
							d.item = sortedData[d.index]; // item to data
							return d;
						}),
					(d) => d.item.slug
				)
				.join(
					(enter) =>
						enter
							.append("g")
							.attr("opacity", 0)
							.attr(
								"transform",
								(d) =>
									`rotate(${rotateAngle(
										middleAngle(d.startAngle, d.endAngle)
									)}) translate(${scoringInnerRadius},0)`
							)
							.call((enter) =>
								enter
									.transition(t)
									.duration(2000)
									.delay((_, i) => (i * 0.02 + delay1) * 1000)
									.attr("opacity", 1)
							),
					(update) =>
						update.call((update) =>
							update
								.transition(sortTransition)
								.duration(durationSortChange * 1000)
								.delay(delaySort1 * 1000)
								.attr("opacity", 0)
								.transition(sortTransition)
								.duration(0)
								.delay(0)
								.attr(
									"transform",
									(d) =>
										`rotate(${rotateAngle(
											middleAngle(d.startAngle, d.endAngle)
										)}) translate(${scoringInnerRadius},0)`
								)
								.transition(sortTransition)
								.duration(durationSortChange * 1000)
								.delay((_, i) => (i * 0.02 + delaySort1) * 1000)
								.attr("opacity", 1)
						),
					(exit) => exit.call((exit) => exit.attr("opacity", 0).remove())
				)
				.attr("class", "score");

			score
				.selectAll("circle")
				.data(
					(d) => {
						const dataArray = [];
						for (let i = 0; i < 10; i++) {
							dataArray.push(d);
						}
						return dataArray;
					},
					(d, i) => `${d.item.slug}-${i}`
				)
				.join("circle")
				.attr("fill", (d) => catColor(d.item.category))
				.attr("opacity", (d, i) => (d.item.score >= i + 1 ? 1 : 0.1))
				.attr("r", () => {
					const totalLengthOfCircleLine =
						scoringOuterRadius - scoringInnerRadius;
					const radius = totalLengthOfCircleLine * 0.05;
					return radius - scale(1);
				})
				.attr("transform", (_, i) => {
					const totalLengthOfCircleLine =
						scoringOuterRadius - scoringInnerRadius;
					const radius = totalLengthOfCircleLine * 0.05;
					return `translate(${(i + 0.5) * radius * 2}, 0)`;
				});

			// text
			const itemText = diagramGroup
				.select(".items-text-group")
				.attr("font-size", textSize)
				.attr("font-family", "sans-serif")
				.selectAll("g")
				.data(
					chords.groups
						.filter((el) => el.value > 0)
						.map((d) => {
							d.item = sortedData[d.index]; // item to data
							return d;
						}),
					(d) => d.item.slug
				)
				.join(
					(enter) =>
						enter
							.append("g")
							.attr("fill-opacity", 0)
							.attr(
								"transform",
								(d) =>
									`rotate(${rotateAngle(
										middleAngle(d.startAngle, d.endAngle)
									)}) translate(${textRadius},0)`
							)
							.call((enter) =>
								enter
									.transition(t)
									.duration(2000)
									.delay((_, i) => (i * 0.02 + delay2) * 1000)
									.attr("fill-opacity", 1)
							),
					(update) =>
						update.call((update) =>
							update
								.transition(sortTransition)
								.duration(durationSortChange * 1000)
								.delay(delaySort2 * 1000)
								.attr("fill-opacity", 0)
								.transition(sortTransition)
								.delay(0)
								.duration(0)
								.attr(
									"transform",
									(d) =>
										`rotate(${rotateAngle(
											middleAngle(d.startAngle, d.endAngle)
										)}) translate(${textRadius},0)`
								)
								.transition(sortTransition)
								.duration(durationSortChange * 1.5 * 1000)
								.delay((_, i) => (i * 0.02 + delaySort2) * 1000)
								.attr("fill-opacity", 1)
						),
					(exit) => exit.call((exit) => exit.attr("fill-opacity", 0).remove())
				)
				.attr("class", "item-text");

			itemText
				.selectAll("text")
				.data(
					(d) => [d],
					(d) => d.item.slug
				)
				.join(
					(enter) =>
						enter
							.append("text")
							.attr("transform", (d) => {
								const angle = middleAngle(d.startAngle, d.endAngle);
								return angle > Math.PI ? "rotate(180)" : null;
							})
							.attr("text-anchor", (d) => {
								const angle = middleAngle(d.startAngle, d.endAngle);
								return angle > Math.PI ? "end" : "start";
							}),
					(update) =>
						update.call((update) =>
							update
								.transition(sortTransition)
								.duration(0)
								.delay((durationSortChange + delaySort2) * 1000)
								.attr("transform", (d) => {
									const angle = middleAngle(d.startAngle, d.endAngle);
									return angle > Math.PI ? "rotate(180)" : null;
								})
								.attr("text-anchor", (d) => {
									const angle = middleAngle(d.startAngle, d.endAngle);
									return angle > Math.PI ? "end" : "start";
								})
						)
				)
				.attr("fill", "#fff")
				.attr("dy", "0.35em")
				.text((d) => {
					const name = d.item.title;
					return name;
				});

			itemText
				.selectAll("rect")
				.data(
					(d) => [d],
					(d) => d.item.slug
				)
				.join("rect")
				.attr("fill", "rgba(0,0,0,0)")
				.attr("height", textSize + textPadding)
				.attr("width", (_, i, n) => {
					const rectEl = n[i];
					const textEl = rectEl.parentNode.childNodes[0];
					const textWidth = textEl.getComputedTextLength();
					return textWidth + textPadding * 2;
				})
				.attr(
					"transform",
					`translate(${-textPadding}, ${(textSize + textPadding) * -0.5})`
				)
				.attr("class", "technology-rect")
				.on("mouseover", handleMouseOverText)
				.on("mouseout", handleMouseOutText)
				.on("click", handleClickText);

			// categories
			diagramGroup
				.select(".categories-group")
				.call((update) =>
					update
						.transition(sortTransition)
						.duration(durationSortChange * 1000)
						.attr("opacity", 0)
						.transition(sortTransition)
						.duration(durationSortChange * 1000)
						.attr("opacity", sort === "category" ? 1 : 0)
				)
				.selectAll("path")
				.data(categories)
				.join(
					(enter) =>
						enter
							.append("path")
							.attr("opacity", 0)
							.attr("d", (d) => {
								// get start item and end item
								const startItemIndex = sortedData.findIndex(
									(item) => item.category === d
								);
								const endItemIndex = findLastIndex(
									sortedData,
									(item) => item.category === d
								);

								const startAngle =
									middleAngle(
										chords.groups.find((el) => el.index === startItemIndex)
											.startAngle,
										chords.groups.find((el) => el.index === startItemIndex)
											.endAngle
									) - 0.03;
								const endAngle =
									middleAngle(
										chords.groups.find((el) => el.index === endItemIndex)
											.startAngle,
										chords.groups.find((el) => el.index === endItemIndex)
											.endAngle
									) + 0.03;

								const arcObject = {
									startAngle,
									endAngle,
								};

								return catArcGenerator(arcObject);
							})
							.call((enter) =>
								enter
									.transition(t)
									.duration(4000)
									.delay(delay2 * 1000)
									.attr("opacity", 1)
							),
					(update) =>
						update.call((update) =>
							update
								.transition(sortTransition)
								.duration(durationSortChange * 1000)
								.delay(delaySort3 * 1000)
								.attr("opacity", 0)
								.transition(sortTransition)
								.duration(0)
								.delay(0)
								.attr("d", (d) => {
									// get start item and end item
									const startItemIndex = sortedData.findIndex(
										(item) => item.category === d
									);
									const endItemIndex = findLastIndex(
										sortedData,
										(item) => item.category === d
									);

									const startAngle =
										middleAngle(
											chords.groups.find((el) => el.index === startItemIndex)
												.startAngle,
											chords.groups.find((el) => el.index === startItemIndex)
												.endAngle
										) - 0.03;
									const endAngle =
										middleAngle(
											chords.groups.find((el) => el.index === endItemIndex)
												.startAngle,
											chords.groups.find((el) => el.index === endItemIndex)
												.endAngle
										) + 0.03;

									const arcObject = {
										startAngle,
										endAngle,
									};

									return catArcGenerator(arcObject);
								})
								.transition(sortTransition)
								.duration(durationSortChange * 1.5 * 1000)
								.delay(delaySort3 * 1000)
								.attr("opacity", 1)
						),
					(exit) => exit.call((exit) => exit.attr("opacity", 0).remove())
				)
				.attr("class", "category")
				.attr("fill", (d) => catColor(d));

			const alphabeticallySortedChords = orderBy(chords, (item) =>
				makeSorted([
					sortedData[item.source.index].slug,
					sortedData[item.target.index].slug,
				])
			);

			// relationship lines
			const relationshipLines = diagramGroup
				.select(".relationship-lines-group")
				.selectAll("g")
				.data(
					alphabeticallySortedChords.map((d, i) => {
						d.sourceItem = sortedData[d.source.index]; // item to data
						d.targetItem = sortedData[d.target.index]; // item to data
						d.i = i;
						return d;
					}),
					(d) =>
						makeSorted(
							[d.sourceItem.slug, d.targetItem.slug],
							([a, b]) => `${a}-${b}`
						)
				)
				.join("g")
				.attr("class", "relationship")
				.attr("opacity", 1);

			const gradient = relationshipLines
				.selectAll("linearGradient")
				.data(
					(d) => [d],
					(d) =>
						makeSorted(
							[d.sourceItem.slug, d.targetItem.slug],
							([a, b]) => `${a}-${b}`
						)
				)
				.join(
					(enter) =>
						enter
							.append("linearGradient")
							.attr("x1", (d) =>
								makeSorted(
									[
										{ key: d.sourceItem.slug, value: d.source.index },
										{ key: d.targetItem.slug, value: d.target.index },
									],
									([a]) => gradientPointGenerator(a.value).x
								)
							)
							.attr("x2", (d) =>
								makeSorted(
									[
										{ key: d.sourceItem.slug, value: d.source.index },
										{ key: d.targetItem.slug, value: d.target.index },
									],
									([, b]) => gradientPointGenerator(b.value).x
								)
							)
							.attr("y1", (d) =>
								makeSorted(
									[
										{ key: d.sourceItem.slug, value: d.source.index },
										{ key: d.targetItem.slug, value: d.target.index },
									],
									([a]) => gradientPointGenerator(a.value).y
								)
							)
							.attr("y2", (d) =>
								makeSorted(
									[
										{ key: d.sourceItem.slug, value: d.source.index },
										{ key: d.targetItem.slug, value: d.target.index },
									],
									([, b]) => gradientPointGenerator(b.value).y
								)
							)
							.attr("id", (d) =>
								makeSorted(
									[d.sourceItem.slug, d.targetItem.slug],
									([a, b]) =>
										`gradient-${convertAlphaNumeric(a)}-${convertAlphaNumeric(
											b
										)}`
								)
							),
					(update) =>
						update.call((update) =>
							update
								.transition(t)
								.delay(durationSortChange * 1000)
								.attr("x1", (d) =>
									makeSorted(
										[
											{ key: d.sourceItem.slug, value: d.source.index },
											{ key: d.targetItem.slug, value: d.target.index },
										],
										([a]) => gradientPointGenerator(a.value).x
									)
								)
								.attr("x2", (d) =>
									makeSorted(
										[
											{ key: d.sourceItem.slug, value: d.source.index },
											{ key: d.targetItem.slug, value: d.target.index },
										],
										([, b]) => gradientPointGenerator(b.value).x
									)
								)
								.attr("y1", (d) =>
									makeSorted(
										[
											{ key: d.sourceItem.slug, value: d.source.index },
											{ key: d.targetItem.slug, value: d.target.index },
										],
										([a]) => gradientPointGenerator(a.value).y
									)
								)
								.attr("y2", (d) =>
									makeSorted(
										[
											{ key: d.sourceItem.slug, value: d.source.index },
											{ key: d.targetItem.slug, value: d.target.index },
										],
										([, b]) => gradientPointGenerator(b.value).y
									)
								)
								.attr("id", (d) =>
									makeSorted(
										[d.sourceItem.slug, d.targetItem.slug],
										([a, b]) =>
											`gradient-${convertAlphaNumeric(a)}-${convertAlphaNumeric(
												b
											)}`
									)
								)
						)
				)
				.attr("gradientUnits", "userSpaceOnUse");

			gradient
				.selectAll("stop")
				.data(
					(d) =>
						makeSorted(
							[
								{ key: d.sourceItem.slug, value: d.sourceItem },
								{ key: d.targetItem.slug, value: d.targetItem },
							],
							([a, b]) => [a.value, b.value]
						),
					(d) => d.slug
				)
				.join(
					(enter) =>
						enter
							.append("stop")
							.attr("offset", (_, i) => (i === 0 ? "0%" : "100%"))
							.attr("stop-color", (d) => catColor(d.category)),
					(update) =>
						update.call((update) =>
							update
								.transition(sortTransition)
								.delay(durationSortChange * 1000)
								.attr("offset", (_, i) => (i === 0 ? "0%" : "100%"))
								.attr("stop-color", (d) => catColor(d.category))
						)
				);

			relationshipLines
				.selectAll("path")
				.data(
					(d) => [d],
					(d) =>
						makeSorted(
							[d.sourceItem.slug, d.targetItem.slug],
							([a, b]) => `${a}-${b}`
						)
				)
				.join(
					(enter) =>
						enter
							.append("path")
							.attr("class", "relationship-line")
							.attr("d", (d) => arcGenerator(chordRadius, chords, d))
							.attr("stroke-dasharray", (d, i, nodes) =>
								nodes[i].getTotalLength()
							)
							.attr("stroke-dashoffset", (d, i, nodes) =>
								nodes[i].getTotalLength()
							)
							.attr("opacity", 1)
							.call((enter) =>
								enter
									.transition("enter")
									.duration(3000)
									.delay((d) => (d.i * 0.005 + delay3) * 1000)
									.attr("stroke-dashoffset", 0)
									.attr("stroke", (d) =>
										makeSorted(
											[d.sourceItem.slug, d.targetItem.slug],
											([a, b]) =>
												`url(#gradient-${convertAlphaNumeric(
													a
												)}-${convertAlphaNumeric(b)})`
										)
									)
									.on("end", (d, i, nodes) => {
										d3.select(nodes[i])
											.attr("stroke-dasharray", null)
											.attr("stroke-dashoffset", null);
									})
							),

					(update) =>
						update.call((update) =>
							update
								.transition(sortTransition)
								.duration(durationSortChange * 1000)
								.attr("opacity", 0)
								.transition(sortTransition)
								.duration(0)
								.delay(0)
								.attr("d", (d) => arcGenerator(chordRadius, chords, d))
								.attr("stroke", (d) =>
									makeSorted(
										[d.sourceItem.slug, d.targetItem.slug],
										([a, b]) =>
											`url(#gradient-${convertAlphaNumeric(
												a
											)}-${convertAlphaNumeric(b)})`
									)
								)
								.transition(sortTransition)
								.duration(durationSortChange * 1000)
								.delay((d) => (d.i * 0.005 + delaySort4) * 1000)
								.attr("opacity", 1)
						)
				)
				.attr("fill", "none")
				.attr("stroke-width", scale(1.5));

			// zoom initialisation
			zoom.current = d3
				.zoom()
				.extent([
					[0, 0],
					[width, height],
				])
				.scaleExtent([minScale, maxScale])
				.on("zoom", handleZoom)
				.on("end", handleZoomEnd);

			svg.call(zoom.current);
		}

		return () => {
			svg.on(".zoom", null);
		};
	}, [
		sort,
		width,
		height,
		minScale,
		maxScale,
		setZoomLevel,
		handleClickText,
		handleZoom,
		delay1,
		delay2,
		delay3,
		durationHover,
		handleZoomEnd,
		handleMouseOverText,
		handleMouseOutText,
	]);

	useEffect(() => {
		if (hoversEnabled) {
			if (temporyHoverDetails) {
				handleMouseOverText(temporyHoverDetails.mousePos, {
					item: temporyHoverDetails.item,
				});
			}
		}
	}, [temporyHoverDetails, hoversEnabled, handleMouseOverText]);
	useEffect(() => {
		const svg = d3.select(ref.current);

		zoom.current?.scaleTo(svg, zoomLevel);
	}, [zoomLevel]);

	const updateRadarWidthAsPercentage = (w, h) => {
		const zoomGroupWidthAsPecentage =
			zoomGroup.current?.node()?.getBBox()?.width / ((width + height) * 0.666);
		radarWidthAsPercentage.current = zoomGroupWidthAsPecentage;
	};

	useEffect(() => {
		let timeout;
		if (sort !== prevSort.current) {
			setHoversEnabled(false);
			let sortDuration =
				durationSortChange * 1000 +
				delaySort2 * 1000 +
				durationSortChange * 1.5 * 1000 +
				((data.length - 1) * 0.02 + delaySort2) * 1000;
			timeout = setTimeout(() => {
				setHoversEnabled(true);
			}, sortDuration);
		}
		return () => {
			clearTimeout(timeout);
		};
	}, [sort]);

	useEffect(() => {
		const callback = () => {
			updateRadarWidthAsPercentage(width, height);
		};
		const initialAnimationGroup = animationGroup.current;

		initialAnimationGroup.addEventListener("animationend", callback);

		const timeout = setTimeout(() => {
			setHoversEnabled(true);
		}, (delay3 + data.length * 0.05) * 1000);

		return () => {
			initialAnimationGroup.addEventListener("animationend", callback);
			clearTimeout(timeout);
		};
	}, [width, height]);

	useEffect(() => {
		prevCategory.current = hoveredCategory;
	}, [hoveredCategory]);
	useEffect(() => {
		prevSort.current = sort;
	}, [sort]);

	return (
		<svg
			ref={ref}
			className={`tech-radar-diagram ${
				modalOpen ? "tech-radar-diagram--modal-open" : ""
			} ${hoveredItem?.title ? "tech-radar-diagram--name-hover" : ""}`}>
			<g className="zoom-group">
				<g
					className="diagram-group"
					style={{ transform: "translate(50%, 50%)" }}>
					<g ref={animationGroup} className="animate-group">
						<circle fill="#000055" opacity="0.6" className="bg-circle" />
						<g className="scores-group" />
						<g className="items-text-group" />
						<g className="categories-group" />
						<g className="relationship-lines-group" />
					</g>
				</g>
			</g>
		</svg>
	);
}
